import {
  Trash,
  NotePencil,
  CaretRight,
  CheckCircle,
  XCircle,
  Hourglass,
  Rocket,
  Eye,
  Briefcase,
} from "@phosphor-icons/react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { getTextMuted, getHoverRowClass } from "../../lib/styleHelpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const STATUS_CONFIG = {
  watching: { label: "Watching", icon: Eye, color: "text-blue-500 bg-blue-500/10 border-blue-500/30", colorLight: "text-blue-600 bg-blue-50 border-blue-200" },
  ready_to_execute: { label: "Ready to Execute", icon: Rocket, color: "text-amber-400 bg-amber-500/10 border-amber-500/30", colorLight: "text-amber-600 bg-amber-50 border-amber-200" },
  executed: { label: "Executed", icon: CheckCircle, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", colorLight: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  passed: { label: "Passed", icon: XCircle, color: "text-gray-400 bg-gray-500/10 border-gray-500/30", colorLight: "text-gray-500 bg-gray-100 border-gray-200" },
};

const ALL_STATUSES = ["watching", "ready_to_execute", "executed", "passed"];

function WatchlistItemNotes({ item, editingId, editNotes, setEditNotes, saveNotes, setEditingId, startEditNotes, isDark }) {
  const inputClass = isDark
    ? "bg-[#0a0a14] border-white/10 text-white placeholder:text-gray-600"
    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400";

  if (editingId === item.id) {
    return (
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          placeholder="Add your notes..."
          className={`flex-1 text-sm px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-1 focus:ring-purple-500 ${inputClass}`}
          data-testid={`watchlist-notes-input-${item.id}`}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") saveNotes(item.id);
            if (e.key === "Escape") setEditingId(null);
          }}
        />
        <Button
          size="sm"
          onClick={() => saveNotes(item.id)}
          className="bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg"
          data-testid={`watchlist-save-notes-${item.id}`}
        >
          Save
        </Button>
      </div>
    );
  }

  if (item.notes) {
    return (
      <button
        onClick={() => startEditNotes(item)}
        className={`text-xs font-body mt-1 px-3 py-1.5 rounded-lg border cursor-pointer text-left block w-full ${
          isDark
            ? "bg-[#0a0a14] border-white/10 text-gray-400 hover:border-purple-500/30"
            : "bg-gray-50 border-gray-200 text-gray-600 hover:border-purple-300"
        }`}
        data-testid={`watchlist-notes-${item.id}`}
      >
        <NotePencil size={12} className="inline mr-1 opacity-50" />
        {item.notes}
      </button>
    );
  }

  return (
    <button
      onClick={() => startEditNotes(item)}
      className={`text-xs font-body mt-1 hover:text-purple-500 transition-colors ${getTextMuted(isDark)}`}
      data-testid={`watchlist-add-note-${item.id}`}
    >
      <NotePencil size={12} className="inline mr-1" />
      Add a note...
    </button>
  );
}

export function WatchlistItem({
  item,
  editingId,
  editNotes,
  setEditNotes,
  saveNotes,
  setEditingId,
  startEditNotes,
  onViewOpportunity,
  onUpdateItem,
  onRemoveItem,
  onTrackInPortfolio,
  portfolioOppIds,
  getProbColor,
  isDark,
}) {
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.watching;
  const StatusIcon = statusCfg.icon;

  return (
    <div className={`p-4 transition-colors ${getHoverRowClass(isDark)}`} data-testid={`watchlist-item-${item.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="outline" className={`text-xs ${isDark ? "border-purple-500/30 text-purple-300" : "border-purple-300 text-purple-600"}`}>
              {item.opportunity_category?.split(" ")[0]}
            </Badge>
            <Badge className={`text-xs ${isDark ? statusCfg.color : statusCfg.colorLight}`}>
              <StatusIcon size={12} className="mr-1" />
              {statusCfg.label}
            </Badge>
          </div>

          <button
            onClick={() => onViewOpportunity(item.opportunity_id)}
            className={`text-left font-bold mb-1 hover:text-purple-500 transition-colors block truncate max-w-full ${isDark ? "text-white" : "text-gray-900"}`}
            data-testid={`watchlist-view-${item.id}`}
          >
            {item.opportunity_title}
          </button>

          <div className="flex items-center gap-3 text-sm mb-2">
            <span className="font-bold" style={{ color: getProbColor(item.opportunity_probability) }}>
              {Math.round(item.opportunity_probability * 100)}%
            </span>
            <span className={isDark ? "text-gray-600" : "text-gray-300"}>|</span>
            <span className="text-emerald-500 text-xs">{item.opportunity_roi}</span>
            <span className={isDark ? "text-gray-600" : "text-gray-300"}>|</span>
            <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Added {new Date(item.added_at).toLocaleDateString()}
            </span>
          </div>

          <WatchlistItemNotes
            item={item}
            editingId={editingId}
            editNotes={editNotes}
            setEditNotes={setEditNotes}
            saveNotes={saveNotes}
            setEditingId={setEditingId}
            startEditNotes={startEditNotes}
            isDark={isDark}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {item.status === "executed" && !portfolioOppIds?.has(item.opportunity_id) && (
            <button
              onClick={() => onTrackInPortfolio(item)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors"
              data-testid={`watchlist-track-portfolio-${item.id}`}
            >
              <Briefcase size={14} />
              Track in Portfolio
            </button>
          )}
          {item.status === "executed" && portfolioOppIds?.has(item.opportunity_id) && (
            <Badge className="text-xs text-emerald-400 bg-emerald-500/10 border-emerald-500/30">
              <Briefcase size={12} className="mr-1" />
              In Portfolio
            </Badge>
          )}
          <Select value={item.status} onValueChange={(val) => onUpdateItem(item.id, { status: val })}>
            <SelectTrigger
              className={`h-8 w-[150px] text-xs rounded-lg ${isDark ? "border-white/10 bg-[#0a0a14]" : "border-gray-200 bg-gray-50"}`}
              data-testid={`watchlist-status-${item.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.map((s) => {
                const cfg = STATUS_CONFIG[s];
                const SIcon = cfg.icon;
                return (
                  <SelectItem key={s} value={s}>
                    <span className="flex items-center gap-1.5"><SIcon size={14} />{cfg.label}</span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <div className="flex gap-1">
            <button
              onClick={() => onViewOpportunity(item.opportunity_id)}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-white/10 text-gray-500 hover:text-purple-400" : "hover:bg-gray-100 text-gray-400 hover:text-purple-600"}`}
              title="View opportunity"
              data-testid={`watchlist-goto-${item.id}`}
            >
              <CaretRight size={16} />
            </button>
            <button
              onClick={() => onRemoveItem(item.id)}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-red-500/10 text-gray-500 hover:text-red-400" : "hover:bg-red-50 text-gray-400 hover:text-red-500"}`}
              title="Remove from watchlist"
              data-testid={`watchlist-remove-${item.id}`}
            >
              <Trash size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
